//Login Page - Using firebase Password/Email Login

import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import Input from '../ui/forms/fields/input';
import PrimaryButton from '../ui/buttons/primaryButton';
import { signInUser } from '../../services/auth';
import { useAlert } from '../../contexts/alertContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthTemplate from './authTemplate';
import { getUserByEmail } from '../../services/user';
import userService from '../../features/user/userService';

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required')
});

const SignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const { showAlertSuccess, showAlertError } = useAlert();
  const redirect = searchParams.get('redirect');

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const onSignIn = async (data) => {
    if (!isSigningIn) {
      setIsSigningIn(true);
      try {
        await userService.checkRegistrationEmail(data.email);
        const result = await signInUser(data.email, data.password);

        //Check if user profile exists
        if (result && result.user) {
          const user = await getUserByEmail(result.user.email);

          if (!user) {
            navigate(redirect || '/overview');
          }

          showAlertSuccess('Successfully signed in!');
          navigate(redirect || '/overview');
          return;
        }

        showAlertError('Please check user credentials and try again.');
      } catch (err) {
        showAlertError(err?.response?.data?.message || 'Please check user credentials and try again.');
      }

      setIsSigningIn(false);
    }
  };

  return (
    <AuthTemplate title="Welcome back" subtitle="Sign in to manage events, teams, and scoring.">
      <Stack spacing={1.5}>
        <Input
          label="Email"
          variant="outlined"
          autoComplete="email"
          {...register('email')}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <Input
          label="Password"
          variant="outlined"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
        <Box display="flex" justifyContent="flex-end">
          <Link component="button" variant="body2" onClick={() => navigate('/forgot-password')}>
            Forgot password?
          </Link>
        </Box>
        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={isSigningIn}
          onClick={() => handleSubmit(onSignIn)()}>
          {isSigningIn ? 'Signing in...' : 'Sign In'}
        </PrimaryButton>
      </Stack>
      {/*<Button
        variant="outlined"
        color="success"
        startIcon={<PhoneIcon />}
        onClick={() => navigate('/phone-auth')}>
        Sign in with Phone
      </Button>*/}
      <Divider sx={{ m: 2 }} />
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={1}>
        New to Bowlscout?
      </Typography>
      <PrimaryButton fullWidth variant="outlined" color="info" onClick={() => navigate('/signup')}>
        Create an Account
      </PrimaryButton>
    </AuthTemplate>
  );
};

export default SignIn;
